import { Box, Skeleton } from "@outschool/backpack";
import React from "react";

interface CalendarSkeletonProps {
  view?: string;
}

export default function CalendarSkeleton({
  view = "week",
}: CalendarSkeletonProps) {
  const isWeekView = view === "week";
  const daysToShow = isWeekView ? 7 : 35;
  const hoursToShow = isWeekView ? 16 : 0;

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Skeleton
            width={100}
            height={44}
            sx={{ borderRadius: "99.9rem", transform: "scale(1)" }}
          />
          <Skeleton variant="text" width={300} height={40} />
        </Box>
      </Box>
      <Box
        sx={{
          borderBottom: ".1rem solid",
          borderColor: "grey.100",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: isWeekView
              ? "60px repeat(7, 1fr)"
              : "repeat(7, 1fr)",
            borderBottom: ".1rem solid",
            borderColor: "grey.100",
          }}
        >
          {isWeekView && (
            <Box
              sx={{
                padding: 8,
                borderRight: ".1rem solid",
                borderColor: "grey.100",
              }}
            />
          )}
          {Array.from({ length: 7 }).map((_, index) => (
            <Box
              key={`header-${index}`}
              sx={{
                padding: 8,
                height: 60,
                textAlign: "center",
                borderRight: index < 6 ? ".1rem solid" : "none",
                borderColor: "grey.100",
              }}
            >
              <Skeleton
                variant="text"
                width="60%"
                height={20}
                sx={{ mx: "auto" }}
              />
            </Box>
          ))}
        </Box>
        {isWeekView ? (
          <Box sx={{ display: "flex" }}>
            <Box
              sx={{
                width: 60,
                borderRight: ".1rem solid",
                borderColor: "grey.100",
              }}
            >
              {Array.from({ length: hoursToShow }).map((_, index) => (
                <Box
                  key={`time-${index}`}
                  sx={{
                    height: 60,
                    padding: 4,
                    borderBottom: ".1rem solid",
                    borderColor: "grey.100",
                    display: "flex",
                    alignItems: "flex-start",
                  }}
                >
                  <Skeleton variant="text" width={40} height={16} />
                </Box>
              ))}
            </Box>
            <Box
              sx={{
                flex: 1,
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
              }}
            >
              {Array.from({ length: 7 }).map((_, dayIndex) => (
                <Box
                  key={`day-${dayIndex}`}
                  sx={{
                    borderRight: dayIndex < 6 ? ".1rem solid" : "none",
                    borderColor: "grey.100",
                  }}
                >
                  {Array.from({ length: hoursToShow }).map((_, hourIndex) => (
                    <Box
                      key={`cell-${dayIndex}-${hourIndex}`}
                      sx={{
                        height: 60,
                        padding: 4,
                        borderBottom: ".1rem solid",
                        borderColor: "grey.100",
                      }}
                    />
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gridTemplateRows: `repeat(${Math.ceil(daysToShow / 7)}, 1fr)`,
            }}
          >
            {Array.from({ length: daysToShow }).map((_, index) => (
              <Box
                key={`month-cell-${index}`}
                sx={{
                  minHeight: 160,
                  padding: 8,
                  borderRight: (index + 1) % 7 !== 0 ? ".1rem solid" : "none",
                  borderBottom: index < daysToShow - 7 ? ".1rem solid" : "none",
                  borderColor: "grey.100",
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                <Skeleton
                  variant="text"
                  width={12}
                  height={20}
                  sx={{
                    marginLeft: "auto",
                  }}
                />
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
